/**
 * <personalization-preview> Web Component
 *
 * Two-line personalization with width-capped input.
 * Font: Arial Italic. Max widths: line1 = 46.97%, line2 = 31.82% of plate.
 * Injects overlays on the gallery carousel image and auto-scrolls to it on focus.
 */
if (!customElements.get('personalization-preview')) {
  class PersonalizationPreview extends HTMLElement {
    connectedCallback() {
      this.triggerValue = this.dataset.triggerValue || 'Personalizzato';
      this.alwaysActive = this.dataset.alwaysActive === 'true';
      this.line1WidthPct = parseFloat(this.dataset.line1WidthPct) || 46.97;
      this.line2WidthPct = parseFloat(this.dataset.line2WidthPct) || 31.82;

      this.input1 = this.querySelector('[data-personalization-input="line1"]');
      this.input2 = this.querySelector('[data-personalization-input="line2"]');
      this.previewText1 = this.querySelector('[data-preview-text="line1"]');
      this.previewText2 = this.querySelector('[data-preview-text="line2"]');
      this.galleryOverlay1 = null;
      this.galleryOverlay2 = null;
      this._coverSlide = null;
      this._carousel = null;

      if (!this.input1 || !this.input2) return;

      this._canvas = document.createElement('canvas');
      this._scrollDebounce = null;

      this.input1.addEventListener('input', () => this._onInput(this.input1, 'line1'));
      this.input2.addEventListener('input', () => this._onInput(this.input2, 'line2'));
      this.input1.addEventListener('focus', this._scrollToCover.bind(this));
      this.input2.addEventListener('focus', this._scrollToCover.bind(this));

      const productContainer = this.closest('product-form') || this.closest('.js-product');
      if (productContainer) {
        productContainer.addEventListener('on:variant:change', this.onVariantChange.bind(this));
      }

      this._checkInitialState();
      this._restoreFromSession();

      requestAnimationFrame(() => {
        this._setupGalleryOverlay();
        if (this.input1.value) this._updatePreview('line1', this.input1.value);
        if (this.input2.value) this._updatePreview('line2', this.input2.value);

        this.productForm = this.input1.form || document.getElementById(this.input1.getAttribute('form'));
        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onFormSubmit.bind(this), true);
        }
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (evt) => {
          if (evt.target === this) {
            this.show();
            if (!this.input1.value) this._updatePreview('line1', 'John Doe');
            if (!this.input2.value) this._updatePreview('line2', 'Coffee Lover');
          }
        });
        document.addEventListener('shopify:block:deselect', (evt) => {
          if (evt.target === this) this._checkInitialState();
        });
      }
    }

    /* ── Gallery overlay + carousel scroll ─────────────────── */

    _setupGalleryOverlay() {
      const coverImg = this.querySelector('.personalization-block__cover-image');
      if (!coverImg) return;

      const src = coverImg.getAttribute('src') || '';
      const match = src.match(/\/([^/?]+?)(?:\?|$)/);
      if (!match) return;
      const filename = match[1];

      const section = this.closest('.js-product') || document;
      const galleryImgs = section.querySelectorAll('.product-media img');
      let targetImg = null;

      for (const img of galleryImgs) {
        if (img.src.includes(filename) || (img.srcset && img.srcset.includes(filename))) {
          targetImg = img;
          break;
        }
      }
      if (!targetImg) return;

      const mediaContainer = targetImg.closest('.product-media--image') || targetImg.closest('.product-media');
      if (!mediaContainer) return;

      this._coverSlide = targetImg.closest('.slider__item');
      this._carousel = targetImg.closest('carousel-slider');

      const topMatch1 = this.previewText1 ? this.previewText1.getAttribute('style').match(/top:\s*([^;]+)/) : null;
      const topMatch2 = this.previewText2 ? this.previewText2.getAttribute('style').match(/top:\s*([^;]+)/) : null;
      const top1 = topMatch1 ? topMatch1[1].trim() : '67%';
      const top2 = topMatch2 ? topMatch2[1].trim() : '72.4%';

      const overlay1 = document.createElement('span');
      overlay1.className = 'personalization-gallery-overlay';
      overlay1.style.top = top1;
      mediaContainer.appendChild(overlay1);

      const overlay2 = document.createElement('span');
      overlay2.className = 'personalization-gallery-overlay';
      overlay2.style.top = top2;
      mediaContainer.appendChild(overlay2);

      this.galleryOverlay1 = overlay1;
      this.galleryOverlay2 = overlay2;
      this._galleryImg = targetImg;

      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(() => {
          this._scaleGalleryFont();
        });
        this._resizeObserver.observe(targetImg);
      }
    }

    _scaleGalleryFont() {
      if (!this._galleryImg) return;
      const imgWidth = this._galleryImg.offsetWidth || this._galleryImg.clientWidth;
      const previewWrapper = this.querySelector('.personalization-block__preview-wrapper');
      const previewWidth = previewWrapper ? previewWrapper.offsetWidth : 320;
      const scale = imgWidth / (previewWidth || 320);
      const galleryFontSize = Math.round(14 * scale);

      if (this.galleryOverlay1) this.galleryOverlay1.style.fontSize = galleryFontSize + 'px';
      if (this.galleryOverlay2) this.galleryOverlay2.style.fontSize = galleryFontSize + 'px';
    }

    _scrollToCover() {
      clearTimeout(this._scrollDebounce);
      this._scrollDebounce = setTimeout(() => {
        if (this._carousel && this._coverSlide && this._carousel.scrollToElement) {
          this._carousel.scrollToElement(this._coverSlide, 'smooth');
        }
      }, 300);
    }

    /* ── Width-capped input ────────────────────────────────── */

    _measureTextWidth(text, fontSize) {
      const ctx = this._canvas.getContext('2d');
      ctx.font = 'italic normal ' + fontSize + 'px Arial, sans-serif';
      return ctx.measureText(text).width;
    }

    _getMaxWidth(lineKey) {
      const previewWrapper = this.querySelector('.personalization-block__preview-wrapper');
      if (!previewWrapper) return Infinity;
      const pct = lineKey === 'line1' ? this.line1WidthPct : this.line2WidthPct;
      return previewWrapper.offsetWidth * (pct / 100);
    }

    _enforceMaxWidth(input, lineKey) {
      const fontSize = 14;
      const maxW = this._getMaxWidth(lineKey);
      if (this._measureTextWidth(input.value, fontSize) > maxW) {
        while (input.value.length > 0 && this._measureTextWidth(input.value, fontSize) > maxW) {
          input.value = input.value.slice(0, -1);
        }
      }
    }

    /* ── Input handling ────────────────────────────────────── */

    _onInput(input, lineKey) {
      this._enforceMaxWidth(input, lineKey);
      this._updatePreview(lineKey, input.value);
      sessionStorage.setItem('philos-' + lineKey, input.value);
    }

    _updatePreview(lineKey, text) {
      const previewSpan = lineKey === 'line1' ? this.previewText1 : this.previewText2;
      const gallerySpan = lineKey === 'line1' ? this.galleryOverlay1 : this.galleryOverlay2;
      if (previewSpan) previewSpan.textContent = text || '';
      if (gallerySpan) gallerySpan.textContent = text || '';
    }

    /* ── Session restore ───────────────────────────────────── */

    _restoreFromSession() {
      if (this.hasAttribute('hidden')) return;
      const saved1 = sessionStorage.getItem('philos-line1');
      const saved2 = sessionStorage.getItem('philos-line2');
      if (saved1) {
        this.input1.value = saved1;
        this._updatePreview('line1', saved1);
      }
      if (saved2) {
        this.input2.value = saved2;
        this._updatePreview('line2', saved2);
      }
    }

    /* ── Variant state ─────────────────────────────────────── */

    _checkInitialState() {
      if (this.alwaysActive) {
        this.show();
        return;
      }
      const picker = document.querySelector('variant-picker');
      if (!picker) return;
      const selectedOptions = picker.selectedOptions;
      if (Array.isArray(selectedOptions) && selectedOptions.includes(this.triggerValue)) {
        this.show();
      } else {
        this.hide();
      }
    }

    onVariantChange(evt) {
      if (this.alwaysActive) return;
      const selectedOptions = evt.detail.selectedOptions;
      if (Array.isArray(selectedOptions) && selectedOptions.includes(this.triggerValue)) this.show();
      else this.hide();
    }

    /* ── Show / Hide ───────────────────────────────────────── */

    show() {
      this.removeAttribute('hidden');
      this.input1.disabled = false;
      this.input2.disabled = false;
      this.input1.required = true;
      this.input2.required = false;
      if (this.galleryOverlay1) this.galleryOverlay1.style.display = '';
      if (this.galleryOverlay2) this.galleryOverlay2.style.display = '';
    }

    hide() {
      this.setAttribute('hidden', '');
      this.input1.disabled = true;
      this.input2.disabled = true;
      this.input1.required = false;
      this.input2.required = false;
      this.input1.value = '';
      this.input2.value = '';
      this._updatePreview('line1', '');
      this._updatePreview('line2', '');
      sessionStorage.removeItem('philos-line1');
      sessionStorage.removeItem('philos-line2');
      if (this.galleryOverlay1) this.galleryOverlay1.style.display = 'none';
      if (this.galleryOverlay2) this.galleryOverlay2.style.display = 'none';
    }

    /* ── Form validation ───────────────────────────────────── */

    onFormSubmit(evt) {
      if (this.input1.disabled) return;

      const empty1 = this.input1.required && !this.input1.value.trim();
      const empty2 = this.input2.required && !this.input2.value.trim();

      if (empty1 || empty2) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        const firstEmpty = empty1 ? this.input1 : this.input2;
        firstEmpty.focus();

        if (empty1) this.input1.classList.add('personalization-block__text-input--error');
        if (empty2) this.input2.classList.add('personalization-block__text-input--error');

        this.input1.addEventListener('input', function() {
          this.classList.remove('personalization-block__text-input--error');
        }, { once: true });
        this.input2.addEventListener('input', function() {
          this.classList.remove('personalization-block__text-input--error');
        }, { once: true });
      }
    }
  }

  customElements.define('personalization-preview', PersonalizationPreview);
}
